import { useCallback, useEffect, useRef, useState } from 'react';
import { useAdminDispatch } from 'admin/reducers';
import { listenGoogleOAuthCallback } from 'admin/components/pages/OAuth/GoogleOAuth';
import {
	getIntegrationToken,
	getUserIntegrations,
	postGoogleOAuth,
	UserIntegrationItem,
} from 'admin/actions/integrations';

const isIntegrationReady = (integration: UserIntegrationItem | null): integration is UserIntegrationItem => {
	return !!integration && integration.status === 'ready';
};

const POPUP_POLL_MS = 400;

/** Picks which integration to select after the list is refetched (e.g. after OAuth). */
export function pickIntegrationAfterRefetch(
	list: UserIntegrationItem[],
	previousIds: ReadonlySet<string>,
	options: { afterOAuth: boolean; pendingOAuthIntegrationId?: string; selectedIntegrationId?: string | null }
): UserIntegrationItem | null {
	if (!list.length) {
		return null;
	}

	if (options.afterOAuth) {
		const newlyAdded = list.filter(i => !previousIds.has(i.id));
		if (newlyAdded.length > 0) {
			return [...newlyAdded].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
		}
		if (options.pendingOAuthIntegrationId) {
			const refreshed = list.find(i => i.id === options.pendingOAuthIntegrationId);
			if (refreshed) {
				return refreshed;
			}
		}
		if (!options.pendingOAuthIntegrationId) {
			return [...list].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
		}
	}

	if (options.selectedIntegrationId) {
		const current = list.find(i => i.id === options.selectedIntegrationId);
		if (current) {
			return current;
		}
	}

	return list.find(o => o.provider === 'google') ?? list[0] ?? null;
}

export const useGoogleUserIntegrations = () => {
	const dispatch = useAdminDispatch();
	const [accessToken, setAccessToken] = useState<Nullable<string>>(null);
	const [userIntegrations, setUserIntegrations] = useState<UserIntegrationItem[]>([]);
	const [userIntegration, setUserIntegrationState] = useState<Nullable<UserIntegrationItem>>(null);
	const [loading, setLoading] = useState<Nullable<string>>(null);
	const integrationIdsRef = useRef<string[]>([]);
	const selectedIntegrationIdRef = useRef<string | null>(null);
	const pendingOAuthIntegrationIdRef = useRef<string | undefined>(undefined);
	const oauthSessionCleanupRef = useRef<(() => void) | null>(null);
	const initialLoadDoneRef = useRef(false);

	const setUserIntegration = useCallback(
		async (nextIntegration: UserIntegrationItem | null = null) => {
			setAccessToken(null);
			setUserIntegrationState(nextIntegration);
			selectedIntegrationIdRef.current = nextIntegration?.id ?? null;

			if (isIntegrationReady(nextIntegration)) {
				const response = await dispatch(getIntegrationToken({ id: nextIntegration.id }));
				if (response.success) {
					setAccessToken(response.result?.accessToken ?? null);
				}
			}
		},
		[dispatch]
	);

	const refetch = useCallback(
		async (afterOAuth = false) => {
			setLoading('init');
			const previousIds = new Set(integrationIdsRef.current);
			const userIntegrationsResponse = await dispatch(getUserIntegrations());
			const list = userIntegrationsResponse.result ?? [];
			setUserIntegrations(list);
			integrationIdsRef.current = list.map(i => i.id);

			const pendingOAuthIntegrationId = pendingOAuthIntegrationIdRef.current;
			if (afterOAuth) {
				pendingOAuthIntegrationIdRef.current = undefined;
			}

			const next = pickIntegrationAfterRefetch(list, previousIds, {
				afterOAuth,
				pendingOAuthIntegrationId,
				selectedIntegrationId: selectedIntegrationIdRef.current,
			});
			await setUserIntegration(next);
			setLoading(null);
		},
		[dispatch, setUserIntegration]
	);

	useEffect(() => {
		if (!initialLoadDoneRef.current) {
			initialLoadDoneRef.current = true;
			refetch(false);
		}
	}, [refetch]);

	useEffect(() => {
		return () => {
			oauthSessionCleanupRef.current?.();
		};
	}, []);

	const signIn = useCallback(
		async (id?: string) => {
			oauthSessionCleanupRef.current?.();
			oauthSessionCleanupRef.current = null;

			pendingOAuthIntegrationIdRef.current = id;
			const oauth = await dispatch(postGoogleOAuth({ id }));
			if (!oauth.result?.url) {
				return;
			}

			const popup = window.open(oauth.result.url, 'popupWindow', 'width=500,height=600');
			if (!popup) {
				return;
			}

			let completed = false;

			const completeOAuth = () => {
				if (completed) {
					return;
				}
				completed = true;
				oauthSessionCleanupRef.current?.();
				oauthSessionCleanupRef.current = null;
				refetch(true);
			};

			const unsubscribe = listenGoogleOAuthCallback(() => {
				completeOAuth();
			});

			const pollTimer = window.setInterval(() => {
				if (popup.closed) {
					completeOAuth();
				}
			}, POPUP_POLL_MS);

			oauthSessionCleanupRef.current = () => {
				completed = true;
				window.clearInterval(pollTimer);
				unsubscribe();
			};
		},
		[dispatch, refetch]
	);

	return {
		userIntegrations,
		userIntegration,
		accessToken,
		loading,
		setUserIntegration,
		signIn,
		refetch,
	};
};
